import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { GeoJSON, Tooltip, Popup, useMap } from 'react-leaflet';
import { categoryLabel } from './Shared';

// ---- geometry helpers (never crash on missing/invalid data) ----
const collectPoints = (coords, out) => {
  if (!Array.isArray(coords)) return;
  if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    out.push([coords[1], coords[0]]); // [lat, lng]
    return;
  }
  coords.forEach((c) => collectPoints(c, out));
};

export const wardBounds = (wards, focusWard = null) => {
  const pts = [];
  (wards || []).forEach((w) => {
    if (!w?.geojson?.coordinates) return;
    if (focusWard && w.ward_number !== focusWard) return;
    collectPoints(w.geojson.coordinates, pts);
  });
  if (!pts.length) return null;
  let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
  pts.forEach(([la, ln]) => {
    if (la < minLat) minLat = la;
    if (la > maxLat) maxLat = la;
    if (ln < minLng) minLng = ln;
    if (ln > maxLng) maxLng = ln;
  });
  return [[minLat, minLng], [maxLat, maxLng]];
};

export const FitBounds = ({ bounds }) => {
  const map = useMap();
  const key = JSON.stringify(bounds);
  useEffect(() => {
    if (bounds) {
      try { map.fitBounds(bounds, { padding: [16, 16] }); } catch { /* ignore invalid bounds */ }
    }
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

// ---- Ward boundary overlays with labels + info popups ----
// Official boundary source: City of Kawartha Lakes Ward Boundaries
// https://www.kawarthalakes.ca/government-administration/elections/ward-boundaries/
export const WardOverlays = ({ wards = [], focusWard = null, showLabels = true, interactive = true, opacityScale = 1, popupRenderer = null }) => {
  const drawable = useMemo(() => (Array.isArray(wards) ? wards : []).filter((w) => w && w.geojson), [wards]);
  return drawable.map((w) => {
    const isFocus = focusWard == null || w.ward_number === focusWard;
    const fillOpacity = Math.min(Math.max((Number(w.overlay_opacity) || 0.18) * (isFocus ? 1 : 0.35) * opacityScale, 0), 1);
    const style = {
      color: w.border_color || '#1a2744',
      weight: Number(w.border_weight) || 2,
      fillColor: w.overlay_color || '#1a2744',
      fillOpacity,
      opacity: isFocus ? 1 : 0.5,
    };
    return (
      <GeoJSON
        key={`ward-${w.ward_number}-${style.color}-${style.weight}-${style.fillColor}-${fillOpacity}-${isFocus}`}
        data={{ type: 'Feature', geometry: w.geojson, properties: { ward: w.ward_number } }}
        style={() => style}
        interactive={interactive}
      >
        {showLabels && (
          <Tooltip permanent direction="center" className="ward-map-label" opacity={1}>
            {w.label || `Ward ${w.ward_number}`}
          </Tooltip>
        )}
        {interactive && popupRenderer && (
          <Popup maxWidth={300}>{popupRenderer(w)}</Popup>
        )}
        {interactive && !popupRenderer && (
          <Popup maxWidth={280}>
            <div className="text-sm" data-testid={`ward-popup-${w.ward_number}`}>
              <p className="font-bold text-base leading-snug">{w.label || `Ward ${w.ward_number}`}</p>
              {w.description && <p className="text-xs text-slate-600 mt-1 leading-relaxed">{w.description}</p>}
              <p className="text-xs text-slate-500 mt-1.5">{w.councillor_note || 'One Councillor is elected per ward.'}</p>
              <div className="mt-2 text-xs space-y-0.5">
                <p><strong>Public issues:</strong> {w.issue_count ?? 0}</p>
                <p><strong>Open:</strong> {w.open_count ?? 0} · <strong>Resolved:</strong> {w.resolved_count ?? 0}</p>
                {w.top_category && <p><strong>Top category:</strong> {categoryLabel(w.top_category)}</p>}
              </div>
              <div className="mt-2 flex flex-col gap-1">
                <Link to={w.ward_number === 8 ? '/issue-tracker' : `/map?ward=${w.ward_number}`} className="text-xs font-bold underline">View issues for this ward</Link>
                {w.source_url && <a href={w.source_url} target="_blank" rel="noreferrer" className="text-[10px] text-slate-500 underline">Source: {w.source_name || 'City of Kawartha Lakes Ward Boundaries'}</a>}
              </div>
            </div>
          </Popup>
        )}
      </GeoJSON>
    );
  });
};

export const WardSourceAttribution = ({ className = '' }) => (
  <p className={`text-[11px] text-slate-500 ${className}`} data-testid="ward-source-attribution">
    Ward boundaries. Source:{' '}
    <a href="https://www.kawarthalakes.ca/government-administration/elections/ward-boundaries/" target="_blank" rel="noreferrer" className="underline">
      City of Kawartha Lakes Ward Boundaries
    </a>
  </p>
);
